"use client";

import React from "react";
import {
  StyledSection,
  ReviewStar,
} from "./customerTestimonials.styles";

export interface ITestimonial {
  quote: string;
  context?: string;
}

interface ICustomerTestimonialsProps {
  title?: string;
  eyebrow?: string;
  testimonials: ITestimonial[];
  reviewsUrl: string;
}

const VerifiedBadge = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z"
      fill="url(#cn-verified-grad)"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GradientDefs = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute", overflow: "hidden" }}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="cn-verified-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#5b5bff" />
        <stop offset="50%" stopColor="#a66cd9" />
        <stop offset="100%" stopColor="#f58a8a" />
      </linearGradient>
    </defs>
  </svg>
);

const StarRow = ({ count = 5 }: { count?: number }) => (
  <div className="stars" aria-label={`Rated ${count} out of 5 stars`}>
    {Array.from({ length: count }).map((_, i) => (
      <ReviewStar key={i} aria-hidden="true" />
    ))}
  </div>
);

export const CustomerTestimonials = ({
  title = "Real reviews. Real customers.",
  eyebrow = "Loved by our customers",
  testimonials,
  reviewsUrl,
}: ICustomerTestimonialsProps) => {
  if (!testimonials.length) return null;

  const [featured, ...rest] = testimonials;

  return (
    <StyledSection>
      <GradientDefs />
      <div className="inner">
        <header className="head">
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
        </header>

        <div className="grid">
          <article className="card featured">
            <div className="card-top">
              <StarRow />
              <span className="verified">
                <VerifiedBadge size={14} />
                Verified review
              </span>
            </div>
            <blockquote className="quote">
              <span aria-hidden="true" className="open-quote">
                “
              </span>
              {featured.quote}
            </blockquote>
            {featured.context && (
              <p className="context">{featured.context}</p>
            )}
          </article>

          {rest.map((t, i) => (
            <article key={i} className="card">
              <div className="card-top">
                <StarRow />
                <span className="verified" title="Verified review">
                  <VerifiedBadge size={12} />
                  Verified
                </span>
              </div>
              <p className="quote">{t.quote}</p>
              {t.context && <p className="context">{t.context}</p>}
            </article>
          ))}
        </div>

        <a
          className="see-all"
          href={reviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Read more customer reviews
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </StyledSection>
  );
};
